import { InsurancePolicyOperationType } from 'src/constants/insurance-policy/enums'
import type { PaymentPeriodType } from 'src/constants/payment/enums'
// Imported as a VALUE, not `import type`: `StepApprovalType.InternalApproval` is read below as
// the create default, matching para-node's own default on the create DTO.
import { StepApprovalType } from 'src/interfaces/onboardingTemplate'
import type {
  CreateInsurancePolicyProductPayload,
  InsurancePolicyProduct,
  UpdateInsurancePolicyProductPayload,
} from 'src/interfaces/insurancePolicy'
import { blankToUndefined } from 'src/utils/blankToUndefined'
import type { InsurancePolicyProductFormValues } from './InsurancePolicyProductForm.interface'

/**
 * Parses a money or percent input. A blank field is `undefined` — "not set" — so the key is
 * omitted from the request entirely; `'0'` is a real zero and must survive.
 */
const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim()

  if (trimmed === '') {
    return undefined
  }

  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed)) {
    return undefined
  }

  return parsed
}

/**
 * Formats a number for input display. A zero is rendered as empty so the placeholder shows,
 * since the backend defaults these columns to 0 and an admin rarely deliberately sets zero.
 * Clearing a money field omits the key entirely from the payload, so the backend keeps its
 * existing value either way — a zero displayed never represents a discarded user input.
 */
const numberToInput = (value: number | null): string => {
  if (value === null || value === undefined || value === 0) {
    return ''
  }

  return String(value)
}

/**
 * A blank input becomes an explicit `null` rather than `undefined` — used only by the UPDATE
 * mapper's clearable text fields, where the key must reach the backend as `null` so the PUT
 * actually overwrites the stored value. `stripUndefined` only strips `undefined`, so `null`
 * survives it through to the request body.
 */
const blankToNull = (value: string): string | null => {
  const trimmed = value.trim()

  if (trimmed === '') {
    return null
  }

  return trimmed
}

/**
 * The four inputs collapsed to the array the backend takes, blanks dropped, order preserved.
 * All four blank yields `undefined` so the key is absent rather than an empty array.
 */
const collectManualUploadRequirements = (
  values: InsurancePolicyProductFormValues,
): string[] | undefined => {
  const entries = [
    values.manualUploadRequirement1,
    values.manualUploadRequirement2,
    values.manualUploadRequirement3,
    values.manualUploadRequirement4,
  ]
    .map((entry) => {
      return entry.trim()
    })
    .filter((entry) => {
      return entry !== ''
    })

  if (entries.length === 0) {
    return undefined
  }

  return entries
}

/**
 * The `operationType` half of the "Lock previously collected documents" toggle. On ⇒
 * `immutable`: the policy is revisioned and everything already collected stays attached to the
 * archived version. Off ⇒ `mutable`: the current version is edited in place and contractors may
 * be asked to resubmit.
 */
const toOperationType = (lockCollectedDocuments: boolean): InsurancePolicyOperationType => {
  if (lockCollectedDocuments) {
    return InsurancePolicyOperationType.Immutable
  }

  return InsurancePolicyOperationType.Mutable
}

/**
 * The optional fields both payloads share. Every blank string becomes `undefined` so the saga
 * omits the key: `coverageDetailsPdfUrl` is `@IsUrl` and would reject `''` outright.
 */
const sharedOptionalFields = (values: InsurancePolicyProductFormValues) => {
  return {
    feeCents: parseOptionalNumber(values.feeCents),
    feePercent: parseOptionalNumber(values.feePercent),
    feeCapCents: parseOptionalNumber(values.feeCapCents),
    insuranceDocusignTemplateId: blankToUndefined(values.insuranceDocusignTemplateId),
    description: blankToUndefined(values.description),
    insuranceRequirements: blankToUndefined(values.insuranceRequirements),
    coverage: blankToUndefined(values.coverage),
    coverageDetails: blankToUndefined(values.coverageDetails),
    coverageDetailsPdfUrl: blankToUndefined(values.coverageDetailsPdfUrl),
    manualUploadRequirements: collectManualUploadRequirements(values),
    requireDocumentUploadWhenOptedIn: values.requireDocumentUploadWhenOptedIn,
    requireExpirationDate: values.requireExpirationDate,
    documentUploadTitleWhenOptedIn: blankToUndefined(values.documentUploadTitleWhenOptedIn),
  }
}

/**
 * The UPDATE-only override for the six free-text fields above: a blank input maps to an
 * explicit `null` so the PUT clears the stored value, rather than CREATE's omit-when-blank
 * (there is nothing to clear on create). `manualUploadRequirements` similarly sends `[]` rather
 * than omitting when all four inputs are blank — the backend's `@IsArray() @ArrayMaxSize(4)`
 * accepts `[]`, and it is what clears the list.
 *
 * `feeCents`, `feePercent` and `feeCapCents` are deliberately NOT overridden here and keep
 * `sharedOptionalFields`'s omit-when-blank behaviour: the form now seeds a stored `0` as a
 * BLANK input (the backend defaults `feeCapCents`/`feePercent` to `0`), so if blank sent `null`
 * here, merely opening a product and pressing Save would flip every defaulted `0` to `null` —
 * and the grid renders `0` as `$0.00` but `null` as an em dash, so untouched products would
 * visibly change. `0` and `null` both mean "charge nothing", so omitting is correct and safe.
 *
 * `insuranceDocusignTemplateId` is also NOT overridden and keeps omit-when-blank: its backend
 * DTO carries `@Transform(({ value }) => value || undefined)`, which converts an incoming
 * `null` to `undefined` before validation runs — the contract simply CANNOT be cleared through
 * this endpoint, so sending `null` here would be a no-op at best.
 */
const updateOnlyClearableFields = (values: InsurancePolicyProductFormValues) => {
  return {
    description: blankToNull(values.description),
    insuranceRequirements: blankToNull(values.insuranceRequirements),
    coverage: blankToNull(values.coverage),
    coverageDetails: blankToNull(values.coverageDetails),
    coverageDetailsPdfUrl: blankToNull(values.coverageDetailsPdfUrl),
    documentUploadTitleWhenOptedIn: blankToNull(values.documentUploadTitleWhenOptedIn),
    manualUploadRequirements: collectManualUploadRequirements(values) ?? [],
  }
}

/**
 * Undefined-valued keys are stripped here rather than left for the saga, so the payload the
 * tests assert on is the payload the request carries.
 */
const stripUndefined = <T extends Record<string, unknown>>(source: T): T => {
  const result = {} as Record<string, unknown>

  Object.keys(source).forEach((key) => {
    if (source[key] !== undefined) {
      result[key] = source[key]
    }
  })

  return result as T
}

export const toCreateInsurancePolicyProductPayload = (
  values: InsurancePolicyProductFormValues,
  insurancePolicyPermanentId: string,
): CreateInsurancePolicyProductPayload => {
  return stripUndefined({
    insurancePolicyPermanentId,
    operationType: toOperationType(values.lockCollectedDocuments),
    insuranceProductId: values.insuranceProductId,
    allowOptOut: values.allowOptOut,
    feeFrequency: values.feeFrequency as PaymentPeriodType,
    approvalType: values.approvalType as StepApprovalType,
    ...sharedOptionalFields(values),
  })
}

/**
 * `insuranceProductId` is carried for the ROUTE only — the saga uses it as the
 * `:insuranceProductId` param and never copies it into the body. `feeFrequency` and
 * `approvalType` are absent entirely: the update DTO does not whitelist either, and
 * `approvalType` is `@ValidateIf(hasOwnProperty)`, so a key present with `undefined` would
 * still be validated.
 */
export const toUpdateInsurancePolicyProductPayload = (
  values: InsurancePolicyProductFormValues,
  insurancePolicyPermanentId: string,
  insuranceProductId: string,
): UpdateInsurancePolicyProductPayload => {
  return stripUndefined({
    insurancePolicyPermanentId,
    insuranceProductId,
    operationType: toOperationType(values.lockCollectedDocuments),
    allowOptOut: values.allowOptOut,
    ...sharedOptionalFields(values),
    // Overrides the six clearable text fields (and manualUploadRequirements) from above with
    // this update-only, null/[]-on-blank behaviour — see the comment on
    // `updateOnlyClearableFields` for exactly which fields keep omit-when-blank instead and why.
    ...updateOnlyClearableFields(values),
  })
}

export const toInsurancePolicyProductFormValues = (
  policyProduct: InsurancePolicyProduct | null,
): InsurancePolicyProductFormValues => {
  const manualUploadRequirements = policyProduct?.manualUploadRequirements ?? []

  return {
    insuranceProductId: policyProduct?.insuranceProductId ?? '',
    allowOptOut: policyProduct?.allowOptOut ?? false,
    requireExpirationDate: policyProduct?.requireExpirationDate ?? true,
    feeCents: numberToInput(policyProduct?.feeCents ?? null),
    feePercent: numberToInput(policyProduct?.feePercent ?? null),
    feeCapCents: numberToInput(policyProduct?.feeCapCents ?? null),
    feeFrequency: policyProduct?.feeFrequency ?? '',
    insuranceDocusignTemplateId: policyProduct?.insuranceDocusignTemplateId ?? '',
    description: policyProduct?.description ?? '',
    insuranceRequirements: policyProduct?.insuranceRequirements ?? '',
    requireDocumentUploadWhenOptedIn: policyProduct?.requireDocumentUploadWhenOptedIn ?? false,
    documentUploadTitleWhenOptedIn: policyProduct?.documentUploadTitleWhenOptedIn ?? '',
    coverage: policyProduct?.coverage ?? '',
    coverageDetails: policyProduct?.coverageDetails ?? '',
    coverageDetailsPdfUrl: policyProduct?.coverageDetailsPdfUrl ?? '',
    // Matches para-node's own default on `AdminInsurancePolicyProductCreateDto`.
    approvalType: StepApprovalType.InternalApproval,
    manualUploadRequirement1: manualUploadRequirements[0] ?? '',
    manualUploadRequirement2: manualUploadRequirements[1] ?? '',
    manualUploadRequirement3: manualUploadRequirements[2] ?? '',
    manualUploadRequirement4: manualUploadRequirements[3] ?? '',
    // Off by default: `mutable` is the cheaper operation, and revisioning the whole policy is
    // the deliberate choice, not the accidental one.
    lockCollectedDocuments: false,
  }
}
